/*
 * Nightcap product page script (module, loaded by sections/nc-build-box.liquid and blocks/nc-pdp-head.liquid).
 *   nc-build-box  "Build the box": tick pieces in or out, the drawing fills up, the total adds up at regular prices,
 *                 the card shows what's typed, one button adds the whole box (factory-kit addToCart, the drawer opens)
 *   price on the Add to cart button follows the picked quantity tier (factory-qty-tiers)
 * Reduced motion and html.factory-lowfx: pieces appear in the box without the drop.
 */

const root = document.documentElement;
const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.classList.contains('factory-lowfx') || !root.classList.contains('nc-anim');

function formatMoney(cents, format) {
  const amount = (Number(cents) || 0) / 100;
  const whole = Number.isInteger(amount);
  const plain = whole ? String(amount) : amount.toFixed(2);
  const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fmt = format || '${{amount}}';
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    if (key === 'amount_no_decimals') return withCommas(String(Math.round(amount)));
    if (key === 'amount_with_comma_separator') return withCommas(whole ? String(amount) : amount.toFixed(2)).replace('.', ',');
    return withCommas(plain);
  });
}

class NcBuildBox extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.money = (c) => formatMoney(c, this.dataset.money);
    this.variants = (this.dataset.variants || '')
      .split(',')
      .filter(Boolean)
      .map((row) => {
        const [id, price, ...title] = row.split(':');
        return { id: Number(id), price: Number(price), title: title.join(':') };
      });
    this.hero = this.variants[0];
    this.items = [...this.querySelectorAll('[data-build-item]')];
    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.tiles = this.querySelector('template[data-tiles]');
    this.msg = this.querySelector('[data-build-msg]');
    this.card = this.querySelector('[data-build-card]');
    this.kit = this.querySelector('[data-build-kit]');
    this.error = this.querySelector('[data-build-error]');
    this.items.forEach((input) => input.addEventListener('change', () => this.toggle(input)));
    this.msg?.addEventListener('input', () => {
      if (this.card) this.card.textContent = this.msg.value.trim() || this.dataset.cardDefault || '';
    });
    this.querySelector('[data-build-add]')?.addEventListener('click', (e) => this.add(e.currentTarget, false));
    this.querySelector('[data-build-kit-add]')?.addEventListener('click', (e) => this.add(e.currentTarget, true));
    // the variant picked in the buy box (Horizon keeps it in the product form's id field)
    this.onPick = () => setTimeout(() => this.syncHero(), 350);
    document.addEventListener('change', this.onPick);
    this.syncHero();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onPick);
  }

  syncHero() {
    const field = document.querySelector('product-form-component input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
    const id = field ? Number(field.value) : NaN;
    const found = this.variants.find((v) => v.id === id);
    if (found) this.hero = found;
    const heroPrice = this.querySelector('[data-build-hero]');
    if (heroPrice && this.hero) heroPrice.textContent = `${this.variants.length > 1 ? `${this.hero.title}, ` : ''}${this.money(this.hero.price)}`;
    this.update();
  }

  toggle(input) {
    const handle = input.value;
    const slot = this.slots.find((s) => s.dataset.slot === handle);
    if (slot) {
      if (input.checked) {
        const tile = this.tiles?.content.querySelector(`[data-tile-for="${CSS.escape(handle)}"]`);
        if (tile) {
          const fresh = tile.cloneNode(true);
          fresh.dataset.slot = handle;
          fresh.style.setProperty('--i', '0');
          if (!still()) fresh.classList.add('is-new-build');
          slot.replaceWith(fresh);
          this.slots[this.slots.indexOf(slot)] = fresh;
          if (!still()) requestAnimationFrame(() => requestAnimationFrame(() => fresh.classList.remove('is-new-build')));
        }
      } else {
        const empty = document.createElement('li');
        empty.className = 'nc-tile nc-tile--empty';
        empty.dataset.slot = handle;
        const swap = () => {
          if (input.checked) return;
          slot.replaceWith(empty);
          this.slots[this.slots.indexOf(slot)] = empty;
        };
        if (still()) swap();
        else {
          slot.classList.add('is-leaving');
          setTimeout(swap, 200);
        }
      }
    }
    this.update();
  }

  chosen() {
    return this.items.filter((i) => i.checked);
  }

  update() {
    const picked = this.chosen();
    const heroCents = this.hero ? this.hero.price : 0;
    const total = picked.reduce((sum, i) => sum + Number(i.dataset.price || 0), heroCents);
    const parts = [`${this.dataset.heroTitle} ${this.money(heroCents)}`, ...picked.map((i) => `${i.dataset.name} ${this.money(i.dataset.price)}`)];
    const set = (sel, text) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = text;
    };
    set('[data-build-total]', this.money(total));
    set('[data-build-parts]', parts.join(' + '));
    set('[data-build-add-price]', this.money(total));
    if (!this.kit) return;
    const needs = (this.dataset.kitNeeds || '').split(',').filter(Boolean);
    const pickedHandles = picked.map((i) => i.value);
    const isKit = needs.length > 0 && this.hero && String(this.hero.id) === this.dataset.kitHero && needs.every((h) => pickedHandles.includes(h));
    this.kit.hidden = !isKit;
    if (!isKit) return;
    const extra = picked.filter((i) => !needs.includes(i.value)).reduce((sum, i) => sum + Number(i.dataset.price || 0), 0);
    const kitPrice = Number(this.dataset.kitPrice || 0);
    let text = (this.dataset.kitText || '')
      .replace('[kit]', this.dataset.kitName || '')
      .replace('[price]', this.money(kitPrice))
      .replace('[compare]', this.money(Number(this.dataset.kitCompare || 0) || total - extra));
    if (extra > 0 && this.dataset.kitExtra) text += ` ${this.dataset.kitExtra.replace('[total]', this.money(kitPrice + extra))}`;
    set('[data-build-kit-txt]', text);
  }

  async add(button, asKit) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const picked = this.chosen();
    const needs = (this.dataset.kitNeeds || '').split(',').filter(Boolean);
    const items = [];
    if (asKit && this.dataset.kitVariant) {
      items.push({ id: Number(this.dataset.kitVariant), quantity: 1 });
      picked.filter((i) => !needs.includes(i.value)).forEach((i) => items.push({ id: Number(i.dataset.variant), quantity: 1 }));
    } else {
      if (this.hero) items.push({ id: this.hero.id, quantity: 1 });
      picked.forEach((i) => items.push({ id: Number(i.dataset.variant), quantity: 1 }));
      if (this.dataset.giftVariant) items.push({ id: Number(this.dataset.giftVariant), quantity: 1 });
    }
    if (!items.length) return;
    button.setAttribute('aria-busy', 'true');
    if (this.error) this.error.hidden = true;
    try {
      const kit = await import(this.dataset.kitJs);
      const result = await kit.addToCart(items, this, 'product');
      if (!result.ok) throw new Error(result.message || '');
      const note = this.msg ? this.msg.value.trim() : '';
      if (note && this.dataset.attrName) {
        const update = ((window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update').replace(/\.js$/, '') + '.js';
        await fetch(update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [this.dataset.attrName]: note } }),
        }).catch(() => {});
      }
    } catch (error) {
      if (this.error) {
        this.error.textContent = error.message || '';
        this.error.hidden = !error.message;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('nc-build-box')) customElements.define('nc-build-box', NcBuildBox);

/* ------------------------------------------------------------------ tier price on the Add to cart button */
function tierPrice(scope) {
  const box = scope.querySelector('factory-qty-tiers input[type="radio"]:checked + .factory-tier__box .factory-tier__price');
  if (!box) return '';
  const node = [...box.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
  return node ? node.textContent.trim() : '';
}
function paintAtc(scope) {
  const price = tierPrice(scope);
  scope.querySelectorAll('product-form-component .add-to-cart-button').forEach((btn) => {
    if (btn.disabled) {
      btn.removeAttribute('data-nc-price');
      return;
    }
    if (price) btn.setAttribute('data-nc-price', price);
    else btn.removeAttribute('data-nc-price');
  });
}
function watchAtc() {
  document.querySelectorAll('factory-qty-tiers').forEach((tiers) => {
    const scope = tiers.closest('.shopify-section') || document;
    if (scope.dataset.ncAtc) return;
    scope.dataset.ncAtc = '1';
    paintAtc(scope);
    scope.addEventListener('change', () => requestAnimationFrame(() => paintAtc(scope)));
    new MutationObserver(() => paintAtc(scope)).observe(scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchAtc);
else watchAtc();
